import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import PageHero from "@/components/PageHero";
import { Leaf, Recycle, Zap, FlaskConical, Trash2, Sun, Users, MessageSquare, BarChart3 } from "lucide-react";

const actions = [
  {
    icon: Recycle,
    text: "Minimera restprodukter från våra processer",
  },
  {
    icon: Zap,
    text: "Ständigt söka åtgärder för att spara energi samt minska energiförbrukning",
  },
  {
    icon: Leaf,
    text: "Använda maskiner och utrustning med hög effektivitet och låg miljöpåverkan",
  },
  {
    icon: Sun,
    text: "Arbeta mot att använda 100% förnybar energi från sol, vind och vatten",
  },
  {
    icon: FlaskConical,
    text: "Utöva ansvarsfull kemikaliehantering där vi strävar efter att minska antalet kemikalier samt byta ut befintliga kemikalier mot miljövänligare alternativ där det är möjligt",
  },
  {
    icon: Trash2,
    text: "Utöva ansvarsfull avfallshantering i samarbete med certifierade och godkända partners",
  },
];

export default function Miljopolicy() {
  return (
    <Layout>
      <SEOHead
        title="Miljöpolicy | ASPAB"
        description="Läs om ASPAB:s miljöpolicy. Vi arbetar aktivt med att minimera vår miljöpåverkan genom energieffektivisering, ansvarsfull kemikaliehantering och hållbara metoder."
        canonical="https://aspab.com/miljopolicy"
        path="/miljopolicy"
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Miljöpolicy", href: "/miljopolicy" },
        ]}

        ogImage="/images/og-miljopolicy_27c53f4d.png"
      />

      <PageHero
        title="Miljöpolicy"
        subtitle="Vårt miljöarbete utgör en naturlig del av verksamheten för ständiga förbättringar."
        breadcrumbs={[
          { name: "Policyer och villkor", href: "/policyer" },
          { name: "Miljöpolicy", href: "/miljopolicy" },
        ]}
      />

      <section className="bg-white py-20 md:py-28">
        <div className="container">
          <div className="max-w-3xl mx-auto">

            <div className="space-y-12">

              {/* Inledning */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Helhetssyn för yttre och inre miljö
                </h2>
                <p className="text-gray-600 mt-4 leading-relaxed">
                  Aspab AB:s verksamhet ska kännetecknas av en helhetssyn för yttre och inre miljö.
                  Vårt miljöarbete utgör en naturlig del av verksamheten för ständiga förbättringar.
                </p>
                <p className="text-gray-600 mt-3 leading-relaxed">
                  Vi tar vårt miljöansvar genom att följa tillämpliga lagar och förordningar samt
                  kundkrav. Vi försöker ständigt förbättra vår miljöprestanda, minska förbrukning
                  av naturresurser samt minska vår miljöpåverkan.
                </p>
              </div>

              {/* Åtgärder */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Detta gör vi genom att
                </h2>
                <div className="mt-6 space-y-4">
                  {actions.map((action, i) => (
                    <div key={i} className="flex gap-4 p-4 bg-gray-50 border border-gray-100 rounded-lg">
                      <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                        <action.icon className="w-5 h-5 text-[#2563EB]" />
                      </div>
                      <p className="text-gray-600 leading-relaxed self-center">{action.text}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Medarbetare */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Medarbetarnas ansvar
                </h2>
                <div className="flex gap-4 mt-4 p-5 bg-blue-50 border border-blue-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                    <Users className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <p className="text-gray-600 leading-relaxed self-center">
                    Vi ansvarar för att utbilda och motivera våra medarbetare att utföra sina
                    arbetsuppgifter på ett för miljön ansvarsfullt sätt. Varje anställd hos oss
                    tar i sin tur ansvar för att följa rutiner och instruktioner samt vara
                    uppmärksam på att genast rapportera eventuella risker mot den inre eller yttre miljön.
                  </p>
                </div>
              </div>

              {/* Dialog */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Aktiv dialog med intressenter
                </h2>
                <div className="flex gap-4 mt-4 p-5 bg-gray-50 border border-gray-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                    <MessageSquare className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <p className="text-gray-600 leading-relaxed self-center">
                    Vi ska föra en aktiv dialog om vårt miljöarbete med övriga intressenter så som
                    kunder, leverantörer, samarbetspartners och myndigheter för att tillsammans nå
                    gemensamma förbättringar.
                  </p>
                </div>
              </div>

              {/* Uppföljning */}
              <div>
                <h2 className="font-heading text-2xl font-bold text-gray-900">
                  Uppföljning och utvärdering
                </h2>
                <div className="flex gap-4 mt-4 p-5 bg-gray-50 border border-gray-100 rounded-lg">
                  <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-[#2563EB]/10 rounded-lg">
                    <BarChart3 className="w-5 h-5 text-[#2563EB]" />
                  </div>
                  <p className="text-gray-600 leading-relaxed self-center">
                    Miljöfrågor ska vara en integrerad del av den dagliga verksamheten och regelbundet
                    revideras och utvärderas för att ge underlag till det fortsatta miljöarbetet och
                    dess mål.
                  </p>
                </div>
              </div>

              {/* Avslutning */}
              <div className="border-t border-gray-200 pt-8">
                <p className="text-gray-600 leading-relaxed">
                  Genom att ta miljöhänsyn inom vår verksamhet vill vi arbeta för att bevara miljön
                  för framtida generationer samt att genom ett starkt engagemang arbeta för att
                  hållbara metoder bibehålls och utvecklas.
                </p>
              </div>

            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
